import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTable, useSortBy } from "react-table";
import axios from "axios";
import { Table } from "reactstrap";
import { BUDGET_PROGRAM_SUMMARY } from "./columns";
import { TableSkeleton } from "@/shared/components/Skeletons";
import { isScionProgram } from "@/shared/helpers";

const BudgetAndApprovalsSummary = ({ organization, program, rootProgram, approvalsRefreshKey = 0 }) => {
  const [awardData, setAwardsData] = useState(null);
  const [budgetSummary, setBudgetSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingAwards, setPendingAwards] = useState(0);
  const cascadingOn = Number(rootProgram?.use_cascading_approvals) > 0;
  // A referral-submission pending award is created regardless of use_cascading_approvals
  // (ReferralService never reads that flag) — it's the only thing that would ever be
  // pending on a program with cascading approvals off, since every other award type
  // auto-issues immediately in that state (ICOV3-1780).
  const hasApprovals = (cascadingOn || pendingAwards > 0) && !isScionProgram(rootProgram);
  const hasBudgetSummary = Number(rootProgram?.budget_summary) > 0;
  const hasScheduledAwards = Number(rootProgram?.enable_schedule_awards) > 0;

  useEffect(() => {
    if (organization?.id && program?.id) {
      setLoading(true);
      axios
        .get(
          `organization/${organization.id}/program/${program.id}/budgetprogram/currentbudget`
        )
        .then((response) => {
          setAwardsData(response.data);
          setBudgetSummary(
            response.data.cascadingData ? response.data.cascadingData : []
          );
          setLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setLoading(false);
        });
    }
  }, [organization, program]);

  useEffect(() => {
    if (organization?.id && program?.id) {
      axios
        .get(
          `organization/${organization.id}/program/${program.id}/manager-approval-pending-count`,
          cascadingOn ? undefined : { params: { category: "referral" } }
        )
        .then((response) => {
          setPendingAwards(response.data?.managerApprovalPendingCount);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [organization, program, approvalsRefreshKey]);

  const columns = React.useMemo(() => BUDGET_PROGRAM_SUMMARY, []);

  const { getTableProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: budgetSummary,
    },
    useSortBy
  );
  if (loading)
    return (
      <div className="reward-budget-summary reward-budget-summary--loading">
        <TableSkeleton rows={1} columns={4} width={"100%"} height={20} />
      </div>
    );

  return (
    <div className="reward-budget-summary">
      {hasApprovals && (
        <Link className="reward-budget-summary__approval-link" to="/manager/cascading-approvals">
          <span className="reward-budget-summary__approval-label">Awards to approve</span>
          <strong className="reward-budget-summary__approval-value">
            {pendingAwards > 0 ? pendingAwards : 0}
          </strong>
        </Link>
      )}

      {hasBudgetSummary &&
        (budgetSummary.length > 0 ? (
          <div className="reward-budget-summary__table-card">
            <div className="reward-budget-summary__table-labels">
              <span>Monthly</span>
              <span>Yearly</span>
            </div>
            <div className="reward-budget-summary__table-scroll">
              <Table className="reward-budget-summary__table table--stack-mobile" {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup, _i) => (
                    <tr {...headerGroup.getHeaderGroupProps()} key={_i}>
                      {headerGroup.headers.map((column, colIndex) => (
                        <th {...column.getHeaderProps()} key={colIndex}>
                          {column.render("Header")}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell, index) => (
                          <td
                            {...cell.getCellProps()}
                            key={index}
                            data-label={typeof cell.column.Header === "string" ? cell.column.Header : ""}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        ) : (
          <div className="reward-budget-summary__empty">
            <strong>No active budget for this program.</strong>
            <span>Monthly and yearly budget totals will appear here when available.</span>
          </div>
        )
      )}

      {hasScheduledAwards && (
        <div className="reward-budget-summary__schedule">
          <div className="reward-budget-summary__metric">
            <span className="reward-budget-summary__metric-label">Awards Pending</span>
            <strong className="reward-budget-summary__metric-value">
              ${awardData?.award_pendings || 0}
            </strong>
          </div>
          <div className="reward-budget-summary__metric">
            <span className="reward-budget-summary__metric-label">Awards Scheduled</span>
            <strong className="reward-budget-summary__metric-value">
              ${awardData?.award_schedule || 0}
            </strong>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetAndApprovalsSummary;
